import type { ResourceSet } from 'components/IAP/hooks/resourceSetHooks';
import type { GroupRosterItem } from 'hooks/incident/useRoster';
import {
  resolveResourceAutomationRuleChecklists,
  type AutomationRuleTypeRef,
} from '../EventsTab/automationRuleChecklists';
import { normalizeAssetDocumentFolders } from './assetDocumentFolders';
import type { ResourceRow } from './ResourceTabCard';

export function geoJsonCoordinates(
  value: unknown
): [number, number] | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const coordinates = (value as { coordinates?: unknown }).coordinates;
  if (
    !Array.isArray(coordinates) ||
    typeof coordinates[0] !== 'number' ||
    typeof coordinates[1] !== 'number'
  ) {
    return undefined;
  }
  return [coordinates[0], coordinates[1]];
}

/** The same row the Assets tab shows in its details dialog. */
export function resourceSetToRow(
  resourceSet: ResourceSet,
  groupMembers: GroupRosterItem[],
  rules?: AutomationRuleTypeRef[]
): ResourceRow {
  const point =
    geoJsonCoordinates(resourceSet.geometry) ??
    geoJsonCoordinates(resourceSet.geolocation);
  const pocMember = resourceSet.point_of_contact
    ? groupMembers.find((m) => m.user_guid === resourceSet.point_of_contact)
    : undefined;
  const resolved = resolveResourceAutomationRuleChecklists(resourceSet, rules);
  return {
    id: resourceSet.id,
    resource_set_id: resourceSet.resource_set_id,
    name: resourceSet.name,
    kind: resourceSet.kind,
    type: resourceSet.type || '',
    assetStatus: resourceSet.asset_status || '',
    assetStatusTimestamp: resourceSet.asset_status_timestamp || '',
    incident: resourceSet.incident_details?.[0]
      ? {
          id: resourceSet.incident_details[0].id,
          name: resourceSet.incident_details[0].name,
        }
      : null,
    incident_details: resourceSet.incident_details,
    incidents: resourceSet.incidents,
    owning_organization: resourceSet.owning_organization || '',
    description: resourceSet.description,
    capabilities: resourceSet.capabilities ?? null,
    datetimeOrdered: resourceSet.date_time_ordered,
    datetimeOrderedOffset: resourceSet.datetime_ordered_offset,
    quantity: resourceSet.quantity,
    status: resourceSet.current_status || resourceSet.status,
    inRequest: resourceSet.in_request,
    aor: resourceSet.aor,
    opcon: resourceSet.opcon,
    tacon: resourceSet.tacon,
    persons_on_board: resourceSet.persons_on_board,
    hull_tail_number: resourceSet.hull_tail_number,
    unit: resourceSet.unit,
    address: resourceSet.address,
    symbology: resourceSet.symbology,
    latitude: point?.[1],
    longitude: point?.[0],
    assignees: resourceSet.resource_in_incident_details
      ? resourceSet.resource_in_incident_details.flatMap((detail) =>
          (detail.assignees || []).map((assignee) => ({
            userGuid: assignee.id,
            userName: assignee.name,
            emailAddress: assignee.email,
          }))
        )
      : [],
    eta: resourceSet.eta,
    etaTimezoneOffset: resourceSet.eta_timezone_offset,
    notes: resourceSet.notes,
    threats: resolved.automationRules,
    threat_checklists: resolved.automationRuleChecklists,
    automation_rules: resolved.automationRules,
    automation_rule_checklists: resolved.automationRuleChecklists,
    arcgis_location_link: resourceSet.arcgis_location_link ?? null,
    image_url: resourceSet.image_url ?? null,
    document_folders: normalizeAssetDocumentFolders(
      resourceSet.document_folders
    ),
    linked_ics213rr: resourceSet.linked_ics213rr ?? null,
    cost: resourceSet.cost ?? null,
    cost_per_time: resourceSet.cost_per_time ?? null,
    cost_time_unit:
      resourceSet.cost_time_unit === 'hour' ||
      resourceSet.cost_time_unit === 'day'
        ? resourceSet.cost_time_unit
        : null,
    pointOfContact: pocMember
      ? {
          userGuid: pocMember.user_guid,
          emailAddress: pocMember.email_address,
          userName: pocMember.user_name,
        }
      : undefined,
  };
}
